import { Activity, CalendarDays, Presentation, ClipboardList } from 'lucide-react';

/*
 * MD Anderson ICU rotation. Built from the Blackboard ICU Orientation guide.
 * See docs/blackboard-md-anderson.md.
 */

const BB_DOC = (id) => `https://bcm.blackboard.com/ultra/courses/_129_1/document/${id}?view=content&state=view`;
const ICU = BB_DOC('_321796_1');

export const mdaIcu = {
  accent: '#da322a',
  icon: Activity,
  tagline: 'SICU: 06:00 to 18:00, rounds, codes, presentations',
  notesKey: 'notes:md-anderson:icu',

  toolSections: [
    { title: 'Logistics', tools: [
      { id: 'day', title: 'Your Day', subtitle: 'Pre-rounds, rounds, lecture, sign-out', icon: CalendarDays, action: 'guide:day', featured: true },
      { id: 'duties', title: 'Duties & Presentations', subtitle: 'What the resident owns on the SICU', icon: Presentation, action: 'guide:duties' },
      { id: 'start', title: 'Getting Started', subtitle: 'Badge, modules, policies', icon: ClipboardList, action: 'guide:start' },
    ] },
  ],

  locations: {
    groups: [
      { id: 'icu', title: 'SICU', items: [
        { name: 'Neurosurgery rounds', floor: 'SICU', where: 'Pod A or Pod B', note: 'About 06:15' },
      ] },
    ],
  },

  documents: {
    storageKey: 'mda-icu-pinned-docs',
    groups: [
      { id: 'icu', title: 'ICU', items: [
        { title: 'ICU orientation', subtitle: 'Blackboard', url: ICU },
      ] },
    ],
  },

  guides: {
    day: {
      title: 'Your Day',
      sections: [
        { title: '06:00 to 18:00, no exceptions', blocks: [{ type: 'bullets', items: [
          'No early sign-out. Bring something to study for quiet stretches',
          'Conflicts such as appointments: raise them with Mary Oler and Dr Wegner before the rotation',
        ] }] },
        { title: 'Timeline', blocks: [{ type: 'rows', rows: [
          { label: '06:00–08:30', value: 'Pre-round on the unit', note: 'Not in the team room: catch the surgeons on their rounds for the plan. Sign-out from the moonlighter' },
          { label: 'About 06:15', value: 'Neurosurgery rounds', note: 'Pod A or Pod B' },
          { label: '08:30', value: 'Rounds' },
          { label: '12:00–13:00', value: 'Lecture', note: 'ICU attendings, APPs, pharmacists' },
          { label: '17:30–18:00', value: 'Sign-out to the night moonlighter', note: 'A late admission: one of the three residents stays for it and writes the note' },
        ] }, { type: 'note', text: 'Acute issue or procedure before rounds: text the attending from the work phone. No nights on this rotation.' }] },
        { title: 'Weekend', blocks: [{ type: 'bullets', items: [
          'One weekend a month, 06:00–18:00 Saturday and Sunday, with a pre-call and a post-call day. The administrator can move those if needed',
        ] }] },
      ],
      sources: [{ title: 'MDA ICU Rotation Orientation', href: ICU, detail: 'Blackboard, sign-in required' }],
    },

    duties: {
      title: 'Duties & Presentations',
      sections: [
        { title: 'On the unit', blocks: [{ type: 'bullets', items: [
          'Residents are the primary critical care providers. There are no APPs on the SICU service',
          'Stabilise and round on SICU admissions, manage them through the day',
          'The surgical team is primary and handles floor transfers',
          'Carry the MDA ICU phone at all times',
          'Respond to codes anywhere in the hospital',
          'Procedures: help with or perform any in the unit, with the attending told first so they can supervise. The MICU team sometimes has more',
          'Dr Wegner has note templates for rounding and procedures',
        ] }] },
        { title: 'Presentations', blocks: [{ type: 'bullets', items: [
          'An ICU topic at noon conference at the end of the month',
          'Monthly Critical Care ECHO Review with the other residents and faculty',
        ] }] },
      ],
      sources: [{ title: 'MDA ICU Rotation Orientation', href: ICU, detail: 'Blackboard, sign-in required' }],
    },

    start: {
      title: 'Getting Started',
      sections: [
        { title: 'Before the rotation', blocks: [{ type: 'bullets', items: [
          'Badge paperwork from the MDA administrator’s email. Pick the badge up from HR before day one',
          'First part of the training modules; the rest at orientation',
          'Vacation requests two months ahead',
          'COVID testing is strict: an in-house test may be required after out-of-state travel. The administrator sends current policy before you start',
        ] }] },
        { title: 'Day one', blocks: [{ type: 'bullets', items: [
          'Orientation 07:00–08:00 with the administrator, then the ICU until sign-out at 18:00',
        ] }] },
      ],
      sources: [{ title: 'MDA ICU Rotation Orientation', href: ICU, detail: 'Blackboard, sign-in required' }],
    },
  },
};
